import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
    Box,
    Backdrop,
    Typography,
    TextField,
    Button,
    IconButton,
    Paper,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    RadioGroup,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Alert,
    AlertTitle,
    CircularProgress,
    Chip,
    Grid,
    Divider,
    Stack,
    Card,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
    Person as PersonIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    CameraAlt as CameraAltIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon
} from '@mui/icons-material';
import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';
import CameraCapture from '../shared/CameraCapture';
import incidentService from '../../services/incidentService';
import employeeServiceExtended from '../../services/employeeServiceExtended';
import { initializeDB } from '../../config/db';
import { JOB_STORE, EMPLOYEE_STORE } from '../../config/constants';

const IncidentFormMain = ({ incidentId, incidentData, embedded = false, onClose, onCompletionChange }) => {
    const { id: routeId } = useParams();
    const id = incidentId || routeId; // Use prop if embedded, otherwise use route param
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [incident, setIncident] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [reportBy, setReportBy] = useState(null);
    const [errors, setErrors] = useState([]);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);

    // Modal states
    const [responsibleModalOpen, setResponsibleModalOpen] = useState(false);
    const [supervisorModalOpen, setSupervisorModalOpen] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [actionResponsibleModalOpen, setActionResponsibleModalOpen] = useState(false);
    const [selectedActionIndex, setSelectedActionIndex] = useState(null);

    // Speech recognition states
    const [isListening, setIsListening] = useState({});
    const [speechSupported, setSpeechSupported] = useState(false);
    const recognitionRef = useRef(null);
    const currentFieldRef = useRef(null);

    const autoSaveTimeoutRef = useRef();
    const lastSavedDataRef = useRef(null);

    const { control, handleSubmit, watch, setValue, getValues, formState: { errors: formErrors } } = useForm({
        defaultValues: {
            // Personnel
            responsiblePerson: null,
            supervisors: [],
            
            // Outcome checkboxes
            catastrophic: false,
            hospital: false,
            clinic: false,
            firstAid: false,
            propertyEquipment: false,
            noInjury: false,
            
            // Client awareness
            clientAware: 'yes',
            clientAwareExplanation: '',
            
            // Employee impacts
            employeeImpacts: [],
            
            // Investigation
            investigationDetails: '',
            
            // Photos
            photos: [],
            
            // Cause analysis
            // Cause Analysis - 5 Whys
            rootCause: '',
            why1: '',
            why2: '',
            why3: '',
            why4: '',
            why5: '',
            
            // Preventive actions - start with one empty action
            preventiveActions: [{
                action: '',
                plannedDate: null,
                actualDate: null,
                responsible: []
            }]
        }
    });

    const watchedValues = watch();
    
    // Auto-sync responsiblePerson with first supervisor when supervisors change
    useEffect(() => {
        const current = watchedValues.supervisors || [];
        if (current.length > 0 && !watchedValues.responsiblePerson) {
            setValue('responsiblePerson', current[0]);
        }
    }, [watchedValues.supervisors]);
    
    const { fields: actionFields, append: appendAction, remove: removeAction } = useFieldArray({
        control,
        name: 'preventiveActions'
    });

    useEffect(() => {
        loadInitialData();
    }, [id, incidentData]);

    // Initialize speech recognition
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            setSpeechSupported(true);
            const recognition = new SpeechRecognition();
            // Safari doesn't support continuous mode properly
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.lang = 'en-US';
            recognition.maxAlternatives = 1;

            recognition.onstart = () => {
                console.log('Speech recognition started');
                // Set listening state ONLY after recognition actually starts (fixes iPad race condition)
                if (currentFieldRef.current) {
                    setIsListening({ [currentFieldRef.current]: true });
                }
            };

            recognition.onresult = (event) => {
                const result = event.results[0];
                if (result.isFinal) {
                    const transcript = result[0].transcript;
                    console.log('Final transcript:', transcript);
                    
                    if (transcript && currentFieldRef.current) {
                        const field = currentFieldRef.current;
                        
                        // Handle different field types
                        if (field.startsWith('employeeImpacts.')) {
                            // Employee impact field
                            const index = parseInt(field.split('.')[1]);
                            const currentValue = getValues(`employeeImpacts.${index}.impact`) || '';
                            setValue(`employeeImpacts.${index}.impact`, currentValue + transcript + ' ');
                        } else if (field.startsWith('photo.')) {
                            // Photo description field
                            const index = parseInt(field.split('.')[1]);
                            const currentValue = getValues(`photo.${index}.description`) || '';
                            setValue(`photo.${index}.description`, currentValue + transcript + ' ');
                        } else if (field.startsWith('preventiveActions.')) {
                            // Preventive action field
                            const parts = field.split('.');
                            const index = parseInt(parts[1]);
                            const fieldName = parts[2] || 'action'; // 'action' not 'description'
                            const currentValue = getValues(`preventiveActions.${index}.${fieldName}`) || '';
                            setValue(`preventiveActions.${index}.${fieldName}`, currentValue + transcript + ' ');
                        } else {
                            // Regular field
                            const currentValue = getValues(field) || '';
                            setValue(field, currentValue + transcript + ' ');
                        }
                    }
                }
            };

            recognition.onend = () => {
                console.log('Speech recognition ended');
                // Clear state only if we're ending the current field
                const endingField = currentFieldRef.current;
                if (endingField) {
                    setIsListening(prev => {
                        // Only clear if this field is currently listening
                        if (prev[endingField]) {
                            return {};
                        }
                        return prev;
                    });
                }
                currentFieldRef.current = null;
            };

            recognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening({});
                currentFieldRef.current = null;
            };

            recognitionRef.current = recognition;
        }
        
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, []); // Empty deps - recognition should only be initialized once on mount

    useEffect(() => {
        // Only save if data actually changed
        if (!incident || loading || submitting) return;
        
        const currentDataString = JSON.stringify(watchedValues);
        
        // Skip if data hasn't changed
        if (lastSavedDataRef.current === currentDataString) {
            return;
        }
        
        // Auto-save on any real change with debounce
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }
        
        autoSaveTimeoutRef.current = setTimeout(() => {
            if (!submitting && incident && !loading) {
                // Check one more time if data changed before saving
                const finalDataString = JSON.stringify(watchedValues);
                if (lastSavedDataRef.current !== finalDataString) {
                    saveFormData(watchedValues);
                    lastSavedDataRef.current = finalDataString;
                }
            }
        }, 2000); // Save 2 seconds after last change

        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [watchedValues]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            
            // Use provided incident data if embedded, otherwise load from DB
            let loadedIncident;
            if (embedded && incidentData) {
                loadedIncident = incidentData;
            } else {
                loadedIncident = await incidentService.getById(parseInt(id));
                if (!loadedIncident) {
                    setErrors(['Incident not found. Please complete Step 1 first.']);
                    if (!embedded) {
                        setTimeout(() => {
                            navigate('/incidents');
                        }, 3000);
                    }
                    return;
                }
            }
            setIncident(loadedIncident);
            
            // Check if full report was already submitted (not just initial report)
            if (loadedIncident.status === 'fullReportSubmitted' || loadedIncident.status === 'completed' || loadedIncident.mainReportSubmitted === true) {
                setFormSubmitted(true);
                // Notify parent component that form is already completed
                if (onCompletionChange) {
                    onCompletionChange(true);
                }
            }
            // Don't block form for step1_submitted, pending, or submitted statuses
            
            // Load reporter info from localStorage
            const employeeId = localStorage.getItem('employeeId');
            if (employeeId) {
                const db = await initializeDB();
                const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
                const store = tx.objectStore(EMPLOYEE_STORE);
                const employees = await store.getAll();
                const reporter = employees.find(e => e.employeeId === employeeId);
                if (reporter) {
                    setReportBy(reporter);
                }
            }
            
            // Load jobs
            const jobDb = await initializeDB();
            const jobTx = jobDb.transaction(JOB_STORE, 'readonly');
            const jobStore = jobTx.objectStore(JOB_STORE);
            const jobsData = await jobStore.getAll();
            setJobs(jobsData);
            
            // Set form values based on incident data
            if (loadedIncident.payload) {
                const payload = loadedIncident.payload;
                const { location, employees } = payload;
                
                // Restore all saved form data
                if (payload.responsiblePerson) setValue('responsiblePerson', payload.responsiblePerson);
                if (payload.supervisor) {
                    // Accept both single object and array
                    if (Array.isArray(payload.supervisor)) {
                        setValue('supervisors', payload.supervisor);
                    } else {
                        setValue('supervisors', [payload.supervisor]);
                    }
                }
                
                // Restore outcome checkboxes
                if (payload.catastrophic !== undefined) setValue('catastrophic', payload.catastrophic);
                if (payload.hospital !== undefined) setValue('hospital', payload.hospital);
                if (payload.clinic !== undefined) setValue('clinic', payload.clinic);
                if (payload.firstAid !== undefined) setValue('firstAid', payload.firstAid);
                if (payload.propertyEquipment !== undefined) setValue('propertyEquipment', payload.propertyEquipment);
                if (payload.noInjury !== undefined) setValue('noInjury', payload.noInjury);
                
                // Restore client awareness
                if (payload.clientAware !== undefined) setValue('clientAware', payload.clientAware);
                if (payload.clientAwareExplanation !== undefined) setValue('clientAwareExplanation', payload.clientAwareExplanation);
                
                // Restore investigation details
                if (payload.investigationDetails !== undefined) setValue('investigationDetails', payload.investigationDetails);
                
                // Restore cause analysis
                if (payload.theIncident !== undefined) setValue('theIncident', payload.theIncident);
                if (payload.immediateCause !== undefined) setValue('immediateCause', payload.immediateCause);
                if (payload.contributoryFactors !== undefined) setValue('contributoryFactors', payload.contributoryFactors);
                if (payload.unsafeConditions !== undefined) setValue('unsafeConditions', payload.unsafeConditions);
                if (payload.underlyingCause !== undefined) setValue('underlyingCause', payload.underlyingCause);
                if (payload.rootCause !== undefined) setValue('rootCause', payload.rootCause);
                
                // Restore 5 Why analysis fields
                if (payload.why1 !== undefined) setValue('why1', payload.why1);
                if (payload.why2 !== undefined) setValue('why2', payload.why2);
                if (payload.why3 !== undefined) setValue('why3', payload.why3);
                if (payload.why4 !== undefined) setValue('why4', payload.why4);
                if (payload.why5 !== undefined) setValue('why5', payload.why5);
                
                // Also check in causeAnalysis object if data is structured that way
                if (payload.causeAnalysis) {
                    if (payload.causeAnalysis.why1 !== undefined) setValue('why1', payload.causeAnalysis.why1);
                    if (payload.causeAnalysis.why2 !== undefined) setValue('why2', payload.causeAnalysis.why2);
                    if (payload.causeAnalysis.why3 !== undefined) setValue('why3', payload.causeAnalysis.why3);
                    if (payload.causeAnalysis.why4 !== undefined) setValue('why4', payload.causeAnalysis.why4);
                    if (payload.causeAnalysis.why5 !== undefined) setValue('why5', payload.causeAnalysis.why5);
                }
                
                // Restore preventive actions
                if (payload.preventiveActions && payload.preventiveActions.length > 0) {
                    setValue('preventiveActions', payload.preventiveActions);
                }
                
                // Restore photos
                if (payload.photos && payload.photos.length > 0) {
                    setValue('photos', payload.photos);
                }
                
                // Auto-fill location data if job was selected
                if (location?.type === 'job' && location?.jobId) {
                    // Job details are now handled on backend
                    // No need to set site area, business division, or job number here
                }
                
                // Set employee impacts based on employees from Step 1 or saved data
                if (payload.employeeImpacts && payload.employeeImpacts.length > 0) {
                    // Use saved employee impacts
                    setValue('employeeImpacts', payload.employeeImpacts);
                } else if (employees && employees.length > 0) {
                    // Create new employee impacts from Step 1 employees
                    const fullEmployees = await employeeServiceExtended.getFullEmployeesByNames(employees);
                    
                    const employeeImpacts = fullEmployees.map(emp => ({
                        ...emp,
                        impact: ''
                    }));
                    
                    setValue('employeeImpacts', employeeImpacts);
                }
            }
            
            // Initialize last saved data reference to prevent immediate auto-save
            setTimeout(() => {
                lastSavedDataRef.current = JSON.stringify(watchedValues);
            }, 100);
            
        } catch (error) {
            console.error('Error loading data:', error);
            setErrors(['Failed to load incident data']);
        } finally {
            setLoading(false);
        }
    };

    const toggleSpeechRecognition = async (field) => {
        if (!recognitionRef.current) {
            console.error('Speech recognition not initialized');
            return;
        }
        
        const isCurrentlyListening = isListening[field];
        
        if (isCurrentlyListening) {
            // Stop recognition
            console.log('Stopping speech recognition for field:', field);
            setIsListening({});
            currentFieldRef.current = null;
            
            try {
                recognitionRef.current.abort(); // Use abort for immediate stop
            } catch (error) {
                console.error('Error stopping recognition:', error);
            }
        } else {
            // Start recognition
            console.log('Starting speech recognition for field:', field);
            
            // First stop any running recognition
            if (Object.keys(isListening).length > 0) {
                try {
                    recognitionRef.current.abort();
                } catch (e) {
                    // Ignore
                }
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            try {
                // Request microphone permission
                await navigator.mediaDevices.getUserMedia({ audio: true });

                // Set field reference BEFORE starting (onstart will set state)
                currentFieldRef.current = field;
                // DON'T set isListening here - let onstart do it after successful start

                // Start recognition
                try {
                    recognitionRef.current.start();
                } catch (error) {
                    if (error.name === 'InvalidStateError') {
                        // Already running, abort and retry
                        console.log('Recognition already running, aborting first...');
                        recognitionRef.current.abort();
                        
                        setTimeout(() => {
                            if (currentFieldRef.current === field) {
                                try {
                                    recognitionRef.current.start();
                                    console.log('Recognition restarted successfully');
                                } catch (e) {
                                    console.error('Failed to restart:', e);
                                    setIsListening({});
                                    currentFieldRef.current = null;
                                }
                            }
                        }, 300);
                    } else {
                        console.error('Error starting recognition:', error);
                        setIsListening({});
                        currentFieldRef.current = null;
                    }
                }
            } catch (error) {
                console.error('Microphone permission denied:', error);
                alert('Please allow microphone access to use speech recognition');
                setIsListening({});
                currentFieldRef.current = null;
            }
        }
    };

    const saveFormData = async (data) => {
        try {
            setSaving(true);
            
            const updateData = {
                ...data,
                reportBy: reportBy || {},
                reportDate: new Date().toISOString(),
                typeOfEvent: determineTypeOfEvent()
            };
            
            await incidentService.updateDraft(parseInt(id), updateData);
            
        } catch (error) {
            console.error('Error saving form:', error);
        } finally {
            setSaving(false);
        }
    };

    const determineTypeOfEvent = () => {
        // Prefer new multi-select when present
        const types = Array.isArray(incident?.payload?.incidentTypes)
            ? incident.payload.incidentTypes
            : (incident?.payload?.incidentType ? [incident.payload.incidentType] : []);
        if (types.includes('Property Damage')) return 'Property Damage';
        return 'Injury';
    };

    const onSubmit = async (data) => {
        console.log('onSubmit called with data:', data);
        try {
            setSubmitting(true);
            setErrors([]);
            
            // Validate required fields
            const validationErrors = [];
            
            // Location details are now handled on backend based on job selection
            // No frontend validation needed for site area, business division, or job number
            
            // responsiblePerson is now automatically set from first supervisor
            if (!data.supervisors || data.supervisors.length === 0) {
                validationErrors.push('Supervisor is required');
            }
            if (!data.investigationDetails) {
                validationErrors.push('Investigation details are required');
            }
            
            // Check if at least one outcome is selected
            const outcomeSelected = data.catastrophic || data.hospital || data.clinic || 
                                   data.firstAid || data.propertyEquipment || data.noInjury;
            if (!outcomeSelected) {
                validationErrors.push('Please select at least one outcome');
            }
            
            // Validate employee impacts
            const missingImpacts = data.employeeImpacts.filter(emp => !emp.impact);
            if (missingImpacts.length > 0) {
                validationErrors.push('Please provide impact details for all involved employees');
            }
            
            // Validate photos have descriptions
            const photosWithoutDesc = data.photos.filter(photo => !photo.description);
            if (photosWithoutDesc.length > 0) {
                validationErrors.push('All photos must have descriptions');
            }
            
            if (validationErrors.length > 0) {
                setErrors(validationErrors);
                setValidationDialogOpen(true); // Show validation dialog
                
                // Scroll to first error field after a short delay
                setTimeout(() => {
                    let firstErrorElement = null;
                    
                    // Check which field has error and find it
                    if (!data.supervisors || data.supervisors.length === 0) {
                        firstErrorElement = document.getElementById('supervisor-section');
                    } else if (!data.investigationDetails) {
                        firstErrorElement = document.getElementById('investigation-section');
                    } else if (!outcomeSelected) {
                        firstErrorElement = document.getElementById('outcome-section');
                    } else if (missingImpacts.length > 0) {
                        firstErrorElement = document.getElementById('employee-impacts-section');
                    } else if (photosWithoutDesc.length > 0) {
                        firstErrorElement = document.getElementById('photos-section');
                    }
                    
                    // If no specific element found, try to find any field with error
                    if (!firstErrorElement) {
                        firstErrorElement = document.querySelector('.MuiTextField-root.Mui-error, .MuiFormControl-root.Mui-error');
                    }
                    
                    // Scroll to the error element
                    if (firstErrorElement) {
                        firstErrorElement.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'center' 
                        });
                    }
                }, 100);
                
                setSubmitting(false);
                return;
            }
            
            // Clean photo data - remove File objects that can't be serialized
            const cleanedPhotos = (data.photos || []).map(photo => ({
                dataUrl: photo.dataUrl,
                url: photo.url || photo.dataUrl, // Use dataUrl if url not available
                name: photo.name,
                timestamp: photo.timestamp,
                description: photo.description
            }));
            
            // Prepare submission data
            const submissionData = {
                ...incident.payload,
                ...data,
                supervisor: watchedValues.supervisors || [],
                photos: cleanedPhotos, // Use cleaned photos
                reportBy: reportBy || {},
                reportDate: new Date().toISOString(),
                typeOfEvent: determineTypeOfEvent(),
                outcome: {
                    catastrophic: data.catastrophic,
                    hospital: data.hospital,
                    clinic: data.clinic,
                    firstAid: data.firstAid,
                    propertyEquipment: data.propertyEquipment,
                    noInjury: data.noInjury
                },
                causeAnalysis: {
                    incident: data.theIncident,
                    immediateCause: data.immediateCause,
                    contributoryFactors: data.contributoryFactors,
                    unsafeConditions: data.unsafeConditions,
                    underlyingCause: data.underlyingCause,
                    rootCause: data.rootCause,
                    whatHappened: data.rootCause, // rootCause is actually "What happened"
                    why1: data.why1,
                    why2: data.why2,
                    why3: data.why3,
                    why4: data.why4,
                    why5: data.why5
                },
                // Also save at top level for backward compatibility
                why1: data.why1,
                why2: data.why2,
                why3: data.why3,
                why4: data.why4,
                why5: data.why5,
                preventiveActions: data.preventiveActions // Ensure preventive actions are saved
            };
            
            // If Auto Incident is also selected along with other types,
            // save main report but do NOT finalize the incident yet.
            const types = Array.isArray(incident?.payload?.incidentTypes)
                ? incident.payload.incidentTypes
                : (incident?.payload?.incidentType ? [incident.payload.incidentType] : []);
            const hasAuto = types.includes('Auto Incident');
            const hasNonAuto = types.some(t => t !== 'Auto Incident');

            if (hasAuto && hasNonAuto) {
                const autoData = incident?.autoAccidentData || {};
                const hasAutoContent = autoData && Object.keys(autoData).length > 0;
                if (hasAutoContent) {
                    await incidentService.submitFullReport(parseInt(id), {
                        ...submissionData,
                        status: 'fullReportSubmitted'
                    });
                } else {
                    await incidentService.updateIncident(parseInt(id), {
                        ...submissionData,
                        mainReportSubmitted: true
                    });
                }
            } else {
                // Submit full report to service (handles online/offline)
                await incidentService.submitFullReport(parseInt(id), {
                    ...submissionData,
                    status: 'fullReportSubmitted'
                });
            }
            
            // Update local state
            setFormSubmitted(true);
            
            // Notify parent component of completion
            if (onCompletionChange) {
                onCompletionChange(true);
            }
            
            // Show success message
            alert('Safety Incident Reported successfully');
            
            // Navigate back to incidents list if not embedded
            if (!embedded) {
                navigate('/incidents');
            }
            
        } catch (error) {
            console.error('Error submitting incident:', error);
            if (error?.code === 'OFFLINE_SYNC_QUEUED') {
                alert('No network connection. The report was saved locally and will be sent once you are back online.');
                setErrors(['Report saved locally. It will be submitted automatically once you are back online.']);
            } else {
                alert('Failed to submit incident report. Please try again.');
                setErrors(['Failed to submit incident report. Please try again.']);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleResponsibleSelect = (selected) => {
        setValue('responsiblePerson', selected);
        setResponsibleModalOpen(false);
    };

    const handleSupervisorsSelect = (selectedList) => {
        setValue('supervisors', selectedList || []);
        if (!watchedValues.responsiblePerson && (selectedList || []).length > 0) {
            setValue('responsiblePerson', selectedList[0]);
        }
        setSupervisorModalOpen(false);
    };

    const handlePhotoCapture = (photoData) => {
        const newPhoto = {
            ...photoData,
            description: ''
        };
        
        const currentPhotos = watchedValues.photos || [];
        setValue('photos', [...currentPhotos, newPhoto]);
        setCameraOpen(false);
    };

    const updatePhotoDescription = (index, description) => {
        const photos = [...(watchedValues.photos || [])];
        photos[index].description = description;
        setValue('photos', photos);
    };

    const removePhoto = (index) => {
        const currentPhotos = watchedValues.photos || [];
        const updatedPhotos = currentPhotos.filter((_, i) => i !== index);
        setValue('photos', updatedPhotos);
    };

    const addPreventiveAction = () => {
        appendAction({
            action: '',
            plannedDate: null,
            actualDate: null,
            responsible: []
        });
    };

    const handleActionResponsibleSelect = (selected) => {
        if (selectedActionIndex !== null) {
            setValue(`preventiveActions.${selectedActionIndex}.responsible`, selected);
        }
        setActionResponsibleModalOpen(false);
        setSelectedActionIndex(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!incident) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    <Typography variant="h6" gutterBottom>
                        Incident not found
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        Please complete Step 1 first. Redirecting to incidents list...
                    </Typography>
                    <Button 
                        variant="contained" 
                        onClick={() => navigate('/incidents')}
                        sx={{ mt: 2 }}
                    >
                        Go to Incidents List
                    </Button>
                </Alert>
            </Box>
        );
    }

    // Check if location was pre-selected
    const locationPreselected = incident?.payload?.location?.type === 'job';
    

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Backdrop
                open={submitting}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}
            >
                <CircularProgress color="inherit" />
                <Typography sx={{ ml: 2 }}>Submitting report...</Typography>
            </Backdrop>
            <Box sx={embedded ? {} : {
                position: 'fixed',
                top: '60px',
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                backgroundColor: 'background.default'
            }}>
                {/* Fixed Header - Only show if not embedded */}
                {!embedded && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            borderBottom: 1,
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                            flex: '0 0 auto'
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={2}>
                            <IconButton onClick={() => navigate(`/incidents/${id}/edit`)} color="primary" size="large">
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography variant="h5">Initial Incident Communication</Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Complete Report Form
                                </Typography>
                            </Box>
                        </Box>
                        <Box display="flex" gap={2} alignItems="center">
                            {saving && (
                                <Chip label="Auto-saving..." size="small" color="info" />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Form Content - Action buttons for embedded mode */}
                {embedded && (
                    <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mb: 2 }}>
                        {saving && (
                            <Chip label="Auto-saving..." size="small" color="info" />
                        )}
                    </Box>
                )}

                {/* Scrollable Form Content */}
                <Box sx={embedded ? { p: 0 } : { flex: '1 1 auto', overflow: 'auto', p: 2 }}>
                    <Paper sx={{ maxWidth: 1200, mx: 'auto', p: embedded ? 2 : 3 }}>
                        {errors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors([])}>
                                {errors.map((error, index) => (
                                    <div key={index}>{error}</div>
                                ))}
                            </Alert>
                        )}
                        
                        {/* Show submitted status if already submitted */}
                        {formSubmitted && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                This report has been submitted successfully and cannot be submitted again.
                            </Alert>
                        )}

                        {/* Location details are now automatically populated on backend based on job selection */}

                        {/* Section 2: Personnel - Supervisors (multiple) */}
                        <Typography variant="h6" gutterBottom>Personnel</Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }} id="supervisor-section">
                            {/* Responsible Person is now hidden and automatically set from Supervisor */}
                            <Grid item xs={12}>
                                <Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                        {(watchedValues.supervisors || []).map((sup, idx) => (
                                            <Chip
                                                key={idx}
                                                label={sup.FullName || sup.fullName || sup.name || 'Supervisor'}
                                                onDelete={() => {
                                                    const list = (watchedValues.supervisors || []).filter((_, i) => i !== idx);
                                                    setValue('supervisors', list);
                                                }}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        startIcon={<PersonIcon />}
                                        onClick={() => setSupervisorModalOpen(true)}
                                        disabled={formSubmitted}
                                        sx={{ height: 40 }}
                                    >
                                        {((watchedValues.supervisors || []).length > 0 ? 'Change' : 'Select')} Supervisors *
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider sx={{ mb: 3 }} />

                        {/* Section 3: Incident Outcome - COMPACT CHIPS */}
                        <Typography variant="h6" gutterBottom>Incident Outcome *</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }} id="outcome-section">
                            <Controller
                                name="catastrophic"
                                control={control}
                                render={({ field }) => (
                                    <Chip
                                        label="Catastrophic"
                                        onClick={() => !formSubmitted && field.onChange(!field.value)}
                                        color={field.value ? "error" : "default"}
                                        variant={field.value ? "filled" : "outlined"}
                                        disabled={formSubmitted}
                                    />
                                )}
                            />
                            <Controller
                                name="hospital"
                                control={control}
                                render={({ field }) => (
                                    <Chip
                                        label="Hospital"
                                        onClick={() => !formSubmitted && field.onChange(!field.value)}
                                        color={field.value ? "error" : "default"}
                                        variant={field.value ? "filled" : "outlined"}
                                        disabled={formSubmitted}
                                    />
                                )}
                            />
                            <Controller
                                name="clinic"
                                control={control}
                                render={({ field }) => (
                                    <Chip
                                        label="Clinic/Dr's Care"
                                        onClick={() => !formSubmitted && field.onChange(!field.value)}
                                        color={field.value ? "warning" : "default"}
                                        variant={field.value ? "filled" : "outlined"}
                                        disabled={formSubmitted}
                                    />
                                )}
                            />
                            <Controller
                                name="firstAid"
                                control={control}
                                render={({ field }) => (
                                    <Chip
                                        label="First Aid"
                                        onClick={() => !formSubmitted && field.onChange(!field.value)}
                                        color={field.value ? "info" : "default"}
                                        variant={field.value ? "filled" : "outlined"}
                                        disabled={formSubmitted}
                                    />
                                )}
                            />
                            <Controller
                                name="propertyEquipment"
                                control={control}
                                render={({ field }) => (
                                    <Chip
                                        label="Property/Equipment"
                                        onClick={() => !formSubmitted && field.onChange(!field.value)}
                                        color={field.value ? "warning" : "default"}
                                        variant={field.value ? "filled" : "outlined"}
                                        disabled={formSubmitted}
                                    />
                                )}
                            />
                            <Controller
                                name="noInjury"
                                control={control}
                                render={({ field }) => (
                                    <Chip
                                        label="No Injury/Damage"
                                        onClick={() => !formSubmitted && field.onChange(!field.value)}
                                        color={field.value ? "success" : "default"}
                                        variant={field.value ? "filled" : "outlined"}
                                        disabled={formSubmitted}
                                    />
                                )}
                            />
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        {/* Section 4: Client Awareness - COMPACT */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography variant="h6">Client Awareness</Typography>
                                <Controller
                                    name="clientAware"
                                    control={control}
                                    render={({ field }) => (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Chip
                                                label="Client Aware"
                                                onClick={() => !formSubmitted && field.onChange('yes')}
                                                color={field.value === 'yes' ? "success" : "default"}
                                                variant={field.value === 'yes' ? "filled" : "outlined"}
                                                size="small"
                                                disabled={formSubmitted}
                                            />
                                            <Chip
                                                label="Not Aware"
                                                onClick={() => !formSubmitted && field.onChange('no')}
                                                color={field.value === 'no' ? "warning" : "default"}
                                                variant={field.value === 'no' ? "filled" : "outlined"}
                                                size="small"
                                                disabled={formSubmitted}
                                            />
                                        </Box>
                                    )}
                                />
                            </Box>
                            {watchedValues.clientAware === 'no' && (
                                <Controller
                                    name="clientAwareExplanation"
                                    control={control}
                                    rules={{ required: watchedValues.clientAware === 'no' ? 'Please explain why client was not informed' : false }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Please explain the reason *"
                                            placeholder="Explain why the client has not been made aware..."
                                            error={!!formErrors.clientAwareExplanation}
                                            helperText={formErrors.clientAwareExplanation?.message}
                                            disabled={formSubmitted}
                                            sx={{ mt: 2 }}
                                        />
                                    )}
                                />
                            )}
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        {/* Section 5: People Involved */}
                        <Box id="employee-impacts-section">
                            <Typography variant="h6" gutterBottom>Details of Person(s) Involved in the Incident</Typography>
                            {/* employeeImpacts data */}
                            {watchedValues.employeeImpacts && watchedValues.employeeImpacts.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Position</TableCell>
                                            <TableCell>Contact Details</TableCell>
                                            <TableCell>Impact *</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {watchedValues.employeeImpacts.map((emp, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{emp.FullName || emp.fullName || '-'}</TableCell>
                                                <TableCell>{emp.JobClassification || emp.jobClassification || '-'}</TableCell>
                                                <TableCell>
                                                    {(emp.Phone || emp.phone) && <div>Phone: {emp.Phone || emp.phone}</div>}
                                                    {emp.email && <div>Email: {emp.email}</div>}
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={`employeeImpacts.${index}.impact`}
                                                        control={control}
                                                        rules={{ required: 'Impact is required' }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                fullWidth
                                                                multiline
                                                                rows={2}
                                                                size="small"
                                                                placeholder="Describe impact..."
                                                                error={!!formErrors.employeeImpacts?.[index]?.impact}
                                                                helperText={formErrors.employeeImpacts?.[index]?.impact?.message}
                                                                disabled={formSubmitted}
                                                                sx={{ 
                                                                    '& .MuiInputBase-root': { 
                                                                        fontSize: '0.875rem',
                                                                        padding: '4px 8px'
                                                                    }
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: speechSupported && (
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                onClick={() => toggleSpeechRecognition(`employeeImpacts.${index}.impact`)}
                                                                                color={isListening[`employeeImpacts.${index}.impact`] ? 'error' : 'default'}
                                                                                disabled={formSubmitted}
                                                                                size="small"
                                                                            >
                                                                                {isListening[`employeeImpacts.${index}.impact`] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    )
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                No employees were selected in Step 1
                            </Alert>
                        )}
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        {/* Section 6: Investigation Details */}
                        <Typography variant="h6" gutterBottom>Details of the Incident</Typography>
                        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                            <Typography variant="body2" paragraph>
                                <strong>Include information regarding:</strong>
                            </Typography>
                            <Typography variant="body2">
                                Description of the event, Activities being performed, Equipment, Working environment, 
                                Weather, Workplace layout, PPE.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Evidence to be included & attached:</strong> Witness statements, Photographs, 
                                Measurements / site plans, Method statements and risk assessments, Toolbox talks, 
                                Permits to work, Site plan / layout, Maintenance / service records of equipment / plant check sheets, 
                                Details of any substances or chemicals involved, Health records of person(s) involved, 
                                Training records of person(s) involved.
                            </Typography>
                        </Alert>
                        <Controller
                            name="investigationDetails"
                            control={control}
                            rules={{ required: 'Investigation details are required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    multiline
                                    rows={6}
                                    label="Investigation Details *"
                                    placeholder="Provide detailed investigation information..."
                                    error={!!formErrors.investigationDetails}
                                    helperText={formErrors.investigationDetails?.message}
                                    disabled={formSubmitted}
                                    sx={{ mb: 3 }}
                                    InputProps={{
                                        endAdornment: speechSupported && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => toggleSpeechRecognition('investigationDetails')}
                                                    color={isListening['investigationDetails'] ? 'error' : 'default'}
                                                    disabled={formSubmitted}
                                                >
                                                    {isListening['investigationDetails'] ? <MicOffIcon /> : <MicIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}
                        />
                        <Divider sx={{ mb: 3 }} />

                        {/* Section 7: Photos - CAMERA CAPTURE */}
                        <Typography variant="h6" gutterBottom>Illustrative Photos</Typography>
                        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                            Do not include images of injuries or personnel in this section
                        </Alert>
                        
                        {/* Camera button for ALL devices - ONLY CAMERA */}
                        <Button
                            variant="contained"
                            startIcon={<CameraAltIcon />}
                            onClick={() => setCameraOpen(true)}
                            color="primary"
                            disabled={formSubmitted}
                            sx={{ mb: 2 }}
                        >
                            Take Photo
                        </Button>
                        
                        {/* Photos List */}
                        {watchedValues.photos && watchedValues.photos.length > 0 && (
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {watchedValues.photos.map((photo, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Paper variant="outlined" sx={{ p: 1 }}>
                                            {/* Photo Preview */}
                                            <Box sx={{ 
                                                width: '100%', 
                                                height: 150, 
                                                position: 'relative',
                                                mb: 1,
                                                overflow: 'hidden',
                                                borderRadius: 1,
                                                backgroundColor: 'grey.100'
                                            }}>
                                                <img 
                                                    src={photo.url || photo.dataUrl} 
                                                    alt={`Photo ${index + 1}`}
                                                    style={{ 
                                                        width: '100%', 
                                                        height: '100%', 
                                                        objectFit: 'cover' 
                                                    }}
                                                />
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => removePhoto(index)}
                                                    disabled={formSubmitted}
                                                    sx={{ 
                                                        position: 'absolute', 
                                                        top: 4, 
                                                        right: 4,
                                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255,255,255,1)'
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            
                                            {/* Description Input */}
                                            <TextField
                                                fullWidth
                                                size="small"
                                                multiline
                                                rows={2}
                                                placeholder="Photo description (required) *"
                                                value={photo.description || ''}
                                                onChange={(e) => updatePhotoDescription(index, e.target.value)}
                                                error={!photo.description}
                                                helperText={!photo.description ? 'Description required' : ''}
                                                disabled={formSubmitted}
                                                InputProps={{
                                                    endAdornment: speechSupported && (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => toggleSpeechRecognition(`photo.${index}.description`)}
                                                                color={isListening[`photo.${index}.description`] ? 'error' : 'default'}
                                                                disabled={formSubmitted}
                                                                size="small"
                                                            >
                                                                {isListening[`photo.${index}.description`] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        <Divider sx={{ mb: 3 }} />

                        {/* Section 3: Root Cause Analysis */}
                        <Typography variant="h6" gutterBottom>Section 3: Root Cause Analysis</Typography>
                        
                        {/* Explanation */}
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                How to complete this analysis:
                            </Typography>
                            <Typography variant="body2" paragraph>
                                Start with describing what happened, then keep asking "Why did this happen?" for each answer to drill down to the root cause.
                            </Typography>
                            <Typography variant="caption">
                                Example: Incident → Worker slipped → Floor was wet → Leak wasn't reported → No inspection process → Lack of safety culture
                            </Typography>
                        </Alert>

                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Grid container spacing={3}>
                                {/* What Happened */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main', mb: 1 }}>
                                        WHAT HAPPENED?
                                    </Typography>
                                    <Controller
                                        name="rootCause"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                multiline
                                                rows={2}
                                                size="small"
                                                placeholder="Describe the incident/problem that occurred..."
                                                helperText="Be specific about what actually happened"
                                                disabled={formSubmitted}
                                                InputProps={{
                                                    endAdornment: speechSupported && (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => toggleSpeechRecognition('rootCause')}
                                                                color={isListening['rootCause'] ? 'error' : 'default'}
                                                                disabled={formSubmitted}
                                                                size="small"
                                                            >
                                                                {isListening['rootCause'] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Why 1 - Immediate Cause */}
                                <Grid item xs={12}>
                                    <Box sx={{ pl: 3, borderLeft: '3px solid #2196f3' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                                            1. WHY did this happen? (Immediate Cause)
                                        </Typography>
                                        <Controller
                                            name="why1"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    placeholder="What was the direct/immediate cause?"
                                                    helperText="The most obvious reason"
                                                    disabled={formSubmitted}
                                                    InputProps={{
                                                        endAdornment: speechSupported && (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => toggleSpeechRecognition('why1')}
                                                                    color={isListening['why1'] ? 'error' : 'default'}
                                                                    disabled={formSubmitted}
                                                                    size="small"
                                                                >
                                                                    {isListening['why1'] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Grid>

                                {/* Why 2 - Contributing Factor */}
                                <Grid item xs={12}>
                                    <Box sx={{ pl: 6, borderLeft: '3px solid #4caf50' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main', mb: 1 }}>
                                            2. WHY did that happen? (Contributing Factor)
                                        </Typography>
                                        <Controller
                                            name="why2"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    placeholder="What allowed the immediate cause to occur?"
                                                    helperText="Dig deeper into the cause"
                                                    disabled={formSubmitted}
                                                    InputProps={{
                                                        endAdornment: speechSupported && (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => toggleSpeechRecognition('why2')}
                                                                    color={isListening['why2'] ? 'error' : 'default'}
                                                                    disabled={formSubmitted}
                                                                    size="small"
                                                                >
                                                                    {isListening['why2'] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Grid>

                                {/* Why 3 - System Issue */}
                                <Grid item xs={12}>
                                    <Box sx={{ pl: 9, borderLeft: '3px solid #ff9800' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 1 }}>
                                            3. Why were the contributing factors not controlled?
                                        </Typography>
                                        <Controller
                                            name="why3"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    placeholder="Why were the contributing factors not controlled?"
                                                    helperText="Explain why contributing factors were not properly managed"
                                                    disabled={formSubmitted}
                                                    InputProps={{
                                                        endAdornment: speechSupported && (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => toggleSpeechRecognition('why3')}
                                                                    color={isListening['why3'] ? 'error' : 'default'}
                                                                    disabled={formSubmitted}
                                                                    size="small"
                                                                >
                                                                    {isListening['why3'] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Grid>

                                {/* Why 4 - Management Issue */}
                                <Grid item xs={12}>
                                    <Box sx={{ pl: 12, borderLeft: '3px solid #9c27b0' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'secondary.main', mb: 1 }}>
                                            4. WHY did that happen? (Management/Training Issue)
                                        </Typography>
                                        <Controller
                                            name="why4"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    placeholder="What management or training gap existed?"
                                                    helperText="Consider supervision, training, or resource issues"
                                                    disabled={formSubmitted}
                                                    InputProps={{
                                                        endAdornment: speechSupported && (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => toggleSpeechRecognition('why4')}
                                                                    color={isListening['why4'] ? 'error' : 'default'}
                                                                    disabled={formSubmitted}
                                                                    size="small"
                                                                >
                                                                    {isListening['why4'] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Grid>

                                {/* Why 5 - Root Cause */}
                                <Grid item xs={12}>
                                    <Box sx={{ pl: 15, borderLeft: '3px solid #f44336' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main', mb: 1 }}>
                                            5. WHY did that happen? (ROOT CAUSE)
                                        </Typography>
                                        <Controller
                                            name="why5"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    placeholder="What is the fundamental root cause?"
                                                    helperText="The underlying issue that, if fixed, would prevent recurrence"
                                                    disabled={formSubmitted}
                                                    sx={{ 
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: '#fff3e0'
                                                        }
                                                    }}
                                                    InputProps={{
                                                        endAdornment: speechSupported && (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => toggleSpeechRecognition('why5')}
                                                                    color={isListening['why5'] ? 'error' : 'default'}
                                                                    disabled={formSubmitted}
                                                                    size="small"
                                                                >
                                                                    {isListening['why5'] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                        <Divider sx={{ mb: 3 }} />

                        {/* Section 4: Preventive Actions */}
                        <Typography variant="h6" gutterBottom>
                            Section 4: Corrective & Preventive Actions
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            What actions should be carried out to prevent recurrence?
                        </Typography>
                        
                        {/* Actions List */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            {actionFields.map((field, index) => (
                                <Box key={field.id} sx={{ mb: index < actionFields.length - 1 ? 3 : 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ 
                                            backgroundColor: 'primary.main', 
                                            color: 'white',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 1,
                                            minWidth: 30,
                                            textAlign: 'center'
                                        }}>
                                            {index + 1}
                                        </Typography>
                                        <Box sx={{ flex: 1 }}>
                                            <Controller
                                                name={`preventiveActions.${index}.action`}
                                                control={control}
                                                rules={{ required: index === 0 ? 'At least one action is required' : false }}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        multiline
                                                        rows={2}
                                                        label="Action Description *"
                                                        placeholder="Describe the corrective/preventive action to be taken..."
                                                        error={!!formErrors.preventiveActions?.[index]?.action}
                                                        helperText={formErrors.preventiveActions?.[index]?.action?.message}
                                                        disabled={formSubmitted}
                                                        sx={{ mb: 2 }}
                                                        InputProps={{
                                                            endAdornment: speechSupported && (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        onClick={() => toggleSpeechRecognition(`preventiveActions.${index}.action`)}
                                                                        color={isListening[`preventiveActions.${index}.action`] ? 'error' : 'default'}
                                                                        disabled={formSubmitted}
                                                                        size="small"
                                                                    >
                                                                        {isListening[`preventiveActions.${index}.action`] ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                    />
                                                )}
                                            />
                                            
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={4}>
                                                    <Controller
                                                        name={`preventiveActions.${index}.plannedDate`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <DatePicker
                                                                {...field}
                                                                label="Target Date"
                                                                value={field.value ? dayjs(field.value) : null}
                                                                onChange={(newValue) => {
                                                                    field.onChange(newValue ? newValue.toISOString() : null);
                                                                }}
                                                                disabled={formSubmitted}
                                                                slotProps={{
                                                                    textField: {
                                                                        size: 'small',
                                                                        fullWidth: true
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Controller
                                                        name={`preventiveActions.${index}.actualDate`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <DatePicker
                                                                {...field}
                                                                label="Completion Date"
                                                                value={field.value ? dayjs(field.value) : null}
                                                                onChange={(newValue) => {
                                                                    field.onChange(newValue ? newValue.toISOString() : null);
                                                                }}
                                                                disabled={formSubmitted}
                                                                slotProps={{
                                                                    textField: {
                                                                        size: 'small',
                                                                        fullWidth: true
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                        <Button
                                                            variant="outlined"
                                                            fullWidth
                                                            size="small"
                                                            startIcon={<PersonIcon />}
                                                            onClick={() => {
                                                                setSelectedActionIndex(index);
                                                                setActionResponsibleModalOpen(true);
                                                            }}
                                                            disabled={formSubmitted}
                                                            sx={{ height: 40, fontSize: '0.75rem' }}
                                                        >
                                                            {watchedValues.preventiveActions?.[index]?.responsible?.length > 0 
                                                                ? `${watchedValues.preventiveActions[index].responsible.length} Assigned`
                                                                : 'Select'}
                                                        </Button>
                                                        {watchedValues.preventiveActions?.[index]?.responsible && 
                                                         watchedValues.preventiveActions[index].responsible.length > 0 && (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                                {watchedValues.preventiveActions[index].responsible.map((person, personIndex) => (
                                                                    <Chip
                                                                        key={personIndex}
                                                                        label={person.FullName || person.fullName}
                                                                        onDelete={!formSubmitted ? () => {
                                                                            const updated = [...watchedValues.preventiveActions[index].responsible];
                                                                            updated.splice(personIndex, 1);
                                                                            setValue(`preventiveActions.${index}.responsible`, updated);
                                                                        } : undefined}
                                                                        size="small"
                                                                        sx={{ height: 20, fontSize: '0.75rem' }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        {actionFields.length > 1 && (
                                            <IconButton
                                                size="small"
                                                onClick={() => removeAction(index)}
                                                disabled={formSubmitted}
                                                sx={{ mt: 0.5 }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    {index < actionFields.length - 1 && <Divider />}
                                </Box>
                            ))}
                            
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={addPreventiveAction}
                                disabled={formSubmitted}
                                sx={{ mt: 2 }}
                                fullWidth
                            >
                                Add Another Action
                            </Button>
                        </Paper>
                        
                        {/* Submit Button at the bottom */}
                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<SendIcon />}
                                onClick={() => {
                                    console.log('Submit button clicked');
                                    console.log('Current form errors:', formErrors);
                                    console.log('Current watched values:', watchedValues);
                                    
                                    // Pre-validation check
                                    const validationErrors = [];
                                    
                                    // Check required fields before react-hook-form validation
                                    if (!watchedValues.supervisors || watchedValues.supervisors.length === 0) {
                                        validationErrors.push('Supervisor is required');
                                    }
                                    if (!watchedValues.investigationDetails) {
                                        validationErrors.push('Investigation details are required');
                                    }
                                    
                                    // Check if at least one outcome is selected
                                    const outcomeSelected = watchedValues.catastrophic || watchedValues.hospital || 
                                                          watchedValues.clinic || watchedValues.firstAid || 
                                                          watchedValues.propertyEquipment || watchedValues.noInjury;
                                    if (!outcomeSelected) {
                                        validationErrors.push('Please select at least one outcome');
                                    }
                                    
                                    // Check employee impacts
                                    const missingImpacts = (watchedValues.employeeImpacts || []).filter(emp => !emp.impact);
                                    if (missingImpacts.length > 0) {
                                        validationErrors.push('Please provide impact details for all involved employees');
                                    }
                                    
                                    // Check photo descriptions
                                    const photosWithoutDesc = (watchedValues.photos || []).filter(photo => !photo.description);
                                    if (photosWithoutDesc.length > 0) {
                                        validationErrors.push('All photos must have descriptions');
                                    }
                                    
                                    // If there are validation errors, show dialog immediately
                                    if (validationErrors.length > 0) {
                                        console.log('Validation errors found:', validationErrors);
                                        setErrors(validationErrors);
                                        setValidationDialogOpen(true);
                                        
                                        // Scroll to first error
                                        setTimeout(() => {
                                            let firstErrorElement = null;
                                            
                                            if (!watchedValues.supervisors || watchedValues.supervisors.length === 0) {
                                                firstErrorElement = document.getElementById('supervisor-section');
                                            } else if (!watchedValues.investigationDetails) {
                                                firstErrorElement = document.getElementById('investigation-section');
                                            } else if (!outcomeSelected) {
                                                firstErrorElement = document.getElementById('outcome-section');
                                            } else if (missingImpacts.length > 0) {
                                                firstErrorElement = document.getElementById('employee-impacts-section');
                                            } else if (photosWithoutDesc.length > 0) {
                                                firstErrorElement = document.getElementById('photos-section');
                                            }
                                            
                                            if (firstErrorElement) {
                                                console.log('Scrolling to error element:', firstErrorElement);
                                                firstErrorElement.scrollIntoView({ 
                                                    behavior: 'smooth', 
                                                    block: 'center' 
                                                });
                                            }
                                        }, 100);
                                        
                                        return;
                                    }
                                    
                                    // If no validation errors, proceed with form submission
                                    handleSubmit(onSubmit)();
                                }}
                                disabled={submitting || formSubmitted}
                                sx={{ 
                                    minWidth: 200,
                                    py: 1.5,
                                    fontSize: '1rem'
                                }}
                            >
                                {formSubmitted ? 'Already Submitted' : (submitting ? 'Submitting...' : 'Submit Report')}
                            </Button>
                        </Box>
                        
                    </Paper>
                </Box>

                {/* Modals */}
                {/* Responsible Person modal removed - now automatically set from Supervisor */}

                <SelectEmployeesAndTempsModal
                    open={supervisorModalOpen}
                    onClose={() => setSupervisorModalOpen(false)}
                    onSelect={handleSupervisorsSelect}
                    alreadySelected={watchedValues.supervisors || []}
                />

                <CameraCapture
                    open={cameraOpen}
                    onClose={() => setCameraOpen(false)}
                    onCapture={handlePhotoCapture}
                />

                <SelectEmployeesAndTempsModal
                    open={actionResponsibleModalOpen}
                    onClose={() => {
                        setActionResponsibleModalOpen(false);
                        setSelectedActionIndex(null);
                    }}
                    onSelect={handleActionResponsibleSelect}
                    alreadySelected={selectedActionIndex !== null ? 
                        (watchedValues.preventiveActions?.[selectedActionIndex]?.responsible || []) : []}
                />

                {/* Validation Errors Dialog */}
                <Dialog 
                    open={validationDialogOpen} 
                    onClose={() => setValidationDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ backgroundColor: 'error.main', color: 'white' }}>
                        Form Validation Errors
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        <Alert severity="error">
                            <AlertTitle>Please fix the following errors:</AlertTitle>
                            <Box sx={{ mt: 1 }}>
                                {errors.map((error, index) => (
                                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                        • {error}
                                    </Typography>
                                ))}
                            </Box>
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => {
                                setValidationDialogOpen(false);
                                // Scroll to first error field after dialog closes
                                setTimeout(() => {
                                    const firstErrorElement = document.querySelector('.MuiTextField-root.Mui-error, .MuiFormControl-root.Mui-error, #supervisor-section, #outcome-section, #employee-impacts-section, #investigation-section, #photos-section');
                                    if (firstErrorElement) {
                                        firstErrorElement.scrollIntoView({ 
                                            behavior: 'smooth', 
                                            block: 'center' 
                                        });
                                    }
                                }, 100);
                            }}
                            variant="contained"
                            color="error"
                        >
                            Go to Errors
                        </Button>
                        <Button 
                            onClick={() => setValidationDialogOpen(false)}
                            variant="outlined"
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </LocalizationProvider>
    );
};

export default IncidentFormMain;
